/**
 * The way into Wrapped, first thing in the dashboard's sidebar: the story's
 * own ink card with its blobs bobbing in the corners and the year in four
 * colours, as on the intro. Before release only admins get it, marked draft.
 */

import { Play } from "lucide-react";

import { blobPath } from "./story/stage";
import "./wrapped.css";

const BLOBS = [
  { colour: "blurple", x: 30, y: 24, r: 46, seed: 0.3 },
  { colour: "fuchsia", x: 236, y: 34, r: 36, seed: 1.7 },
  { colour: "yellow", x: 238, y: 226, r: 58, seed: 4.1 },
  { colour: "green", x: 8, y: 236, r: 40, seed: 2.9 },
  { colour: "cyan", x: 250, y: 132, r: 16, seed: 5.3 },
];

const YEAR = ["yellow", "fuchsia", "green", "cyan"];

export function WrappedCard({ title, draft = false }: { title: string; draft?: boolean }) {
  return (
    <a
      href="/wrapped"
      aria-label={`See your ${title} Wrapped`}
      className="wr-way-in group relative block overflow-hidden rounded-2xl px-4 pb-4 pt-3"
      style={{ background: "var(--wr-ink)", color: "var(--wr-white)" }}
    >
      <svg aria-hidden viewBox="0 0 256 260" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        {BLOBS.map((b, i) => (
          <path
            key={b.colour}
            className="wr-way-in-blob"
            style={{ animationDelay: `${-i * 1.7}s` }}
            d={blobPath(b.x, b.y, b.r, 1, b.seed, 0.22)}
            fill={`var(--wr-${b.colour})`}
          />
        ))}
      </svg>
      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] opacity-80" style={{ fontFamily: "var(--wr-font-mono)" }}>
          {draft ? "Draft · only admins see this" : "Your year is in"}
        </p>
        <div className="mt-3 leading-[0.85]" style={{ fontFamily: "var(--wr-font-display)", fontWeight: 800, fontStretch: "75%" }}>
          <p className="text-[34px]">Your</p>
          <p className="break-words text-[64px] tracking-[-0.03em]" aria-hidden>
            {[...title].map((c, i) => (
              <span key={i} style={{ color: `var(--wr-${YEAR[i % YEAR.length]})` }}>
                {c}
              </span>
            ))}
          </p>
          <p className="text-[40px]">Wrapped</p>
        </div>
        <span
          className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-extrabold transition-transform group-hover:-rotate-2"
          style={{ background: "var(--wr-white)", color: "var(--wr-ink)", fontFamily: "var(--wr-font-display)", fontStretch: "85%" }}
        >
          <Play size={15} fill="currentColor" aria-hidden />
          See your Wrapped
        </span>
      </div>
    </a>
  );
}
